import React, {useContext, useState} from "react";
import "./researchExperiences.scss";
import {researchExperiences} from "../../portfolio";
import {Fade} from "react-reveal";
import StyleContext from "../../contexts/StyleContext";

export default function ResearchExperiences() {
  const [activeMediaIndex, setActiveMediaIndex] = useState({});

  function openUrlInNewTab(url) {
    if (!url) {
      return;
    }
    var win = window.open(url, "_blank");
    win.focus();
  }

  const {isDark} = useContext(StyleContext);
  if (!researchExperiences.display) {
    return null;
  }

  // Helper to normalize src from require() results
  const normalizeSrc = (src) => {
    if (src == null) return null; // null or undefined
    // If it's already a string, return it (most common case in CRA)
    if (typeof src === 'string') return src;
    // Handle require() results - webpack may wrap it in an object with default
    if (typeof src === 'object') {
      // Check for default property (common webpack pattern for dynamic imports)
      if (src.default !== undefined && src.default !== null) {
        // If default is a string, return it
        if (typeof src.default === 'string') return src.default;
        // If default is still an object, try to get its default
        if (typeof src.default === 'object' && src.default.default !== undefined) {
          return src.default.default;
        }
        // Otherwise return the default value (might work as-is)
        return src.default;
      }
      // If no default property, return the object itself (React might handle it)
      return src;
    }
    // For any other type, try to convert to string
    return String(src);
  };

  // Helper to detect if a src is a video based on the original require path
  // Since webpack may transform URLs, we check the src value itself
  const detectMediaType = (src, originalSrc) => {
    // Try to get a string representation to check extension
    const srcStr = typeof src === 'string' ? src : 
                   (src && typeof src === 'object' && src.default ? src.default : String(src));
    
    // Check for video extensions in the URL/path
    if (/\.(mov|MOV|mp4|MP4|webm|WEBM|avi|AVI|mkv|MKV)(\?|$)/i.test(srcStr)) {
      return 'video';
    }
    
    // Also check the original source if provided (for debugging)
    if (originalSrc && /\.(mov|MOV|mp4|MP4|webm|WEBM|avi|AVI|mkv|MKV)/i.test(String(originalSrc))) {
      return 'video';
    }
    
    return 'image';
  };

  const getMediaItems = project => {
    if (project.media && project.media.length > 0) {
      return project.media
        .map(item => ({
          type: item.type || 'image',
          src: normalizeSrc(item.src)
        }))
        .filter(item => item.src != null); // Use != to catch both null and undefined
    }
    if (project.images && project.images.length > 0) {
      const items = project.images.map((src, idx) => {
        const normalizedSrc = normalizeSrc(src);
        // Don't filter here - let's see what we get
        const mediaType = detectMediaType(normalizedSrc, src);
        return {type: mediaType, src: normalizedSrc};
      });
      // Only filter out items where src is explicitly null or undefined
      return items.filter(item => item.src != null && item.src !== '');
    }
    if (project.video) {
      const videoSrc = normalizeSrc(project.video);
      return videoSrc ? [{type: "video", src: videoSrc}] : [];
    }
    if (project.image) {
      const imageSrc = normalizeSrc(project.image);
      return imageSrc ? [{type: "image", src: imageSrc}] : [];
    }
    return [];
  };

  const handlePrevMedia = (projectIndex, mediaLength) => {
    setActiveMediaIndex(prev => {
      const current = prev[projectIndex] || 0;
      const nextIndex = (current - 1 + mediaLength) % mediaLength;
      return {...prev, [projectIndex]: nextIndex};
    });
  };

  const handleNextMedia = (projectIndex, mediaLength) => {
    setActiveMediaIndex(prev => {
      const current = prev[projectIndex] || 0;
      const nextIndex = (current + 1) % mediaLength;
      return {...prev, [projectIndex]: nextIndex};
    });
  };

  const GetDescBullets = ({descBullets, isDark}) => {
    return descBullets
      ? descBullets.map((item, i) => (
          <li
            key={i}
            className={isDark ? "subTitle dark-mode-text" : "subTitle"}
          >
            {item}
          </li>
        ))
      : null;
  };

  return (
    <Fade bottom duration={1000} distance="20px">
      <div className="main" id="research">
        <div>
          <h1 className="skills-heading">{researchExperiences.title}</h1>
          <p
            className={
              isDark
                ? "dark-mode project-subtitle"
                : "subTitle project-subtitle"
            }
          >
            {researchExperiences.subtitle}
          </p>

          <div className="projects-container">
            <div className="grid-container">
            {researchExperiences.projects.map((project, i) => {
            const mediaItems = getMediaItems(project);
            const mediaLength = mediaItems.length;
            const currentIndex = activeMediaIndex[i] || 0;
            const currentMedia =
              mediaLength > 0 ? mediaItems[currentIndex] : null;
            
            // Debug: uncomment to check media items
            // console.log(`Project ${i}:`, { mediaItems, mediaLength, currentMedia });

              return (
                <div
                  key={i}
                  className={
                    isDark
                      ? "dark-mode project-card project-card-dark"
                      : "project-card project-card-light"
                  }
                >
                  {currentMedia ? (
                    <div className="project-media-carousel">
                      {mediaLength > 1 && (
                        <button
                          className="carousel-arrow carousel-arrow-left"
                          onClick={() => handlePrevMedia(i, mediaLength)}
                          aria-label="Previous media"
                        >
                          <span>&#8249;</span>
                        </button>
                      )}
                      <div className="project-image">
                        {currentMedia.type === "video" ? (
                          <video
                            src={currentMedia.src}
                            className="card-image"
                            controls
                          />
                        ) : (
                          <img
                            src={currentMedia.src}
                            alt={project.projectName}
                            className="card-image"
                          />
                        )}
                      </div>
                      {mediaLength > 1 && (
                        <button
                          className="carousel-arrow carousel-arrow-right"
                          onClick={() => handleNextMedia(i, mediaLength)}
                          aria-label="Next media"
                        >
                          <span>&#8250;</span>
                        </button>
                      )}
                    </div>
                  ) : null}
                  <div className="project-detail">
                    <h5
                      className={isDark ? "dark-mode card-title" : "card-title"}
                    >
                      {project.projectName}
                    </h5>
                    <p
                      className={
                        isDark ? "dark-mode card-subtitle" : "card-subtitle"
                      }
                    >
                      {project.projectDesc}
                    </p>
                    <ul className="project-desc">
                      <GetDescBullets descBullets={project.descBullets} isDark={isDark} />
                    </ul>
                    {project.footerLink ? (
                      <div className="project-card-footer">
                        {project.footerLink.map((link, i) => {
                          return (
                            <span
                              key={i}
                              className={
                                isDark ? "dark-mode project-tag" : "project-tag"
                              }
                              onClick={() => openUrlInNewTab(link.url)}
                            >
                              {link.name}
                            </span>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        </div>
      </div>
    </Fade>
  );
}
